import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Activity, AlertTriangle, Settings, Search, Trash2, Ban, CheckCircle, Megaphone, Plus, Edit, Lightbulb, MessageSquarePlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserRoleManager } from "@/components/UserRoleManager";
import { AccessAttemptLogs } from "@/components/AccessAttemptLogs";
import { UserModerationPanel } from "@/components/admin/UserModerationPanel";
import { CommunityRequestsAdmin } from "@/components/CommunityRequestsAdmin";
import { GrantsImporter } from "@/components/admin/GrantsImporter";
import { GrantsManagementTable } from "@/components/admin/GrantsManagementTable";

interface AdminUser {
  id: string;
  username: string;
  farm_name: string;
  location: string;
  created_at: string;
  user_id: string;
}

interface ContentReport {
  id: string;
  content_type: string;
  content_id: string;
  reason: string;
  description: string;
  status: string;
  created_at: string;
  reporter_id: string;
}

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: string;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  display_type: string;
  post_frequency: number | null;
}

interface PlatformSuggestion {
  id: string;
  title: string;
  category: string;
  description: string;
  status: string;
  created_at: string;
  user_id: string;
  votes_count: number;
}

export const Admin = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [suggestions, setSuggestions] = useState<PlatformSuggestion[]>([]);
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    totalPosts: 0,
    totalReports: 0,
    activeUsers: 0
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Announcement form state
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    type: 'info',
    is_active: true,
    start_date: '',
    end_date: '',
    display_type: 'frequency',
    post_frequency: 10
  });
  const [editingAnnouncement, setEditingAnnouncement] = useState<string | null>(null);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      // Load users
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (usersError) throw usersError;
      setUsers(usersData || []);

      // Load content reports
      const { data: reportsData, error: reportsError } = await supabase
        .from('content_reports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (reportsError) throw reportsError;
      setReports(reportsData || []);

      // Load announcements
      const { data: announcementsData, error: announcementsError } = await supabase
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false });

      if (announcementsError) throw announcementsError;
      setAnnouncements(announcementsData || []);

      // Load platform suggestions (mock data for now since table doesn't exist yet)
      setSuggestions([
        {
          id: '1',
          title: 'Better crop rotation tracking',
          category: 'feature',
          description: 'Add ability to track crop rotation patterns over multiple seasons',
          status: 'pending',
          created_at: new Date().toISOString(),
          user_id: 'user1',
          votes_count: 15
        },
        {
          id: '2',
          title: 'Weather integration improvements',
          category: 'improvement',
          description: 'More accurate weather forecasting for farm locations',
          status: 'in-progress',
          created_at: new Date().toISOString(),
          user_id: 'user2',
          votes_count: 23
        }
      ]);

      // Load analytics - fix the type issue
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select('id', { count: 'exact', head: true });

      if (postsError) throw postsError;

      const postsCount = postsData?.length || 0;

      setAnalytics({
        totalUsers: usersData?.length || 0,
        totalPosts: postsCount,
        totalReports: reportsData?.length || 0,
        activeUsers: usersData?.filter(u => 
          new Date(u.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        ).length || 0
      });

    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleReportAction = async (reportId: string, action: 'approve' | 'reject') => {
    try {
      const { error } = await supabase
        .from('content_reports')
        .update({ 
          status: action === 'approve' ? 'resolved' : 'dismissed',
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', reportId);

      if (error) throw error;

      toast.success(`Report ${action}d successfully`);
      loadAdminData();
    } catch (error) {
      console.error(`Error ${action}ing report:`, error);
      toast.error(`Failed to ${action} report`);
    }
  };

  const handleAnnouncementSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to create announcements');
        return;
      }

      const announcementData = {
        ...announcementForm,
        created_by: user.id,
        start_date: announcementForm.start_date || null,
        end_date: announcementForm.end_date || null,
        display_type: announcementForm.display_type,
        post_frequency: announcementForm.display_type === 'frequency' ? announcementForm.post_frequency : null
      };

      if (editingAnnouncement) {
        const { error } = await supabase
          .from('announcements')
          .update(announcementData)
          .eq('id', editingAnnouncement);

        if (error) throw error;
        toast.success('Announcement updated successfully');
        setEditingAnnouncement(null);
      } else {
        const { error } = await supabase
          .from('announcements')
          .insert([announcementData]);

        if (error) throw error;
        toast.success('Announcement created successfully');
      }

      setAnnouncementForm({
        title: '',
        content: '',
        type: 'info',
        is_active: true,
        start_date: '',
        end_date: '',
        display_type: 'frequency',
        post_frequency: 10
      });

      loadAdminData();
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error('Failed to save announcement');
    }
  };

  const handleEditAnnouncement = (announcement: Announcement) => {
    setAnnouncementForm({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      is_active: announcement.is_active,
      start_date: announcement.start_date ? announcement.start_date.slice(0, 10) : '',
      end_date: announcement.end_date ? announcement.end_date.slice(0, 10) : '',
      display_type: announcement.display_type || 'frequency',
      post_frequency: announcement.post_frequency || 10
    });
    setEditingAnnouncement(announcement.id);
  };

  const handleDeleteAnnouncement = async (announcementId: string) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', announcementId);

      if (error) throw error;
      toast.success('Announcement deleted successfully');
      loadAdminData();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  const handleToggleAnnouncementStatus = async (announcementId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('announcements')
        .update({ is_active: !currentStatus })
        .eq('id', announcementId);

      if (error) throw error;
      toast.success(`Announcement ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadAdminData();
    } catch (error) {
      console.error('Error toggling announcement status:', error);
      toast.error('Failed to update announcement status');
    }
  };

  const handleSuggestionStatusUpdate = async (suggestionId: string, newStatus: string) => {
    // Mock implementation - in real app this would update the database
    setSuggestions(prev => prev.map(s => 
      s.id === suggestionId ? { ...s, status: newStatus } : s
    ));
    toast.success('Suggestion status updated');
  };

  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.farm_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getReportStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'dismissed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getAnnouncementTypeColor = (type: string) => {
    switch (type) {
      case 'info': return 'bg-blue-100 text-blue-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'success': return 'bg-green-100 text-green-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSuggestionStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-3 space-y-3">
        <div className="text-center">
          <h1 className="text-lg font-bold text-white mb-1">Admin Panel</h1>
          <p className="text-sm text-white/90">Loading admin data...</p>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-24 bg-gray-200 rounded-lg"></div>
          <div className="h-48 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3">
      <div className="text-center mb-4">
        <h1 className="text-lg font-bold text-white mb-1">Admin Panel</h1>
        <p className="text-sm text-white/90">System administration and user management</p>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card>
          <CardContent className="p-3 text-center">
            <Users className="h-6 w-6 mx-auto mb-1 text-blue-600" />
            <div className="text-lg font-bold">{analytics.totalUsers}</div>
            <div className="text-xs text-gray-600">Total Users</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <Activity className="h-6 w-6 mx-auto mb-1 text-green-600" />
            <div className="text-lg font-bold">{analytics.activeUsers}</div>
            <div className="text-xs text-gray-600">Active Users</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <Settings className="h-6 w-6 mx-auto mb-1 text-purple-600" />
            <div className="text-lg font-bold">{analytics.totalPosts}</div>
            <div className="text-xs text-gray-600">Total Posts</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <AlertTriangle className="h-6 w-6 mx-auto mb-1 text-red-600" />
            <div className="text-lg font-bold">{analytics.totalReports}</div>
            <div className="text-xs text-gray-600">Reports</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid grid-cols-4 grid-rows-3 w-full h-auto gap-1 p-1">
          <TabsTrigger value="users" className="text-xs px-2 py-1.5">Users</TabsTrigger>
          <TabsTrigger value="roles" className="text-xs px-2 py-1.5">Roles</TabsTrigger>
          <TabsTrigger value="security" className="text-xs px-2 py-1.5">Security</TabsTrigger>
          <TabsTrigger value="announcements" className="text-xs px-2 py-1.5">Announcements</TabsTrigger>
          <TabsTrigger value="communities" className="text-xs px-2 py-1.5">Communities</TabsTrigger>
          <TabsTrigger value="grants" className="text-xs px-2 py-1.5">Grants</TabsTrigger>
          <TabsTrigger value="suggestions" className="text-xs px-2 py-1.5">Suggestions</TabsTrigger>
          <TabsTrigger value="reports" className="text-xs px-2 py-1.5">Reports</TabsTrigger>
          <TabsTrigger value="settings" className="text-xs px-2 py-1.5">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-3">
          <UserModerationPanel />
        </TabsContent>

        <TabsContent value="roles" className="space-y-3">
          <UserRoleManager />
        </TabsContent>

        <TabsContent value="security" className="space-y-3">
          <AccessAttemptLogs />
        </TabsContent>

        <TabsContent value="announcements" className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Megaphone className="h-4 w-4" />
                Banner Announcements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              {/* Announcement Form */}
              <form onSubmit={handleAnnouncementSubmit} className="space-y-3 p-3 border rounded-lg bg-gray-50">
                <h3 className="text-sm font-medium">
                  {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Title</label>
                    <Input
                      value={announcementForm.title}
                      onChange={(e) => setAnnouncementForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Announcement title"
                      className="h-8 text-xs"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium mb-1">Type</label>
                    <Select 
                      value={announcementForm.type}
                      onValueChange={(value) => setAnnouncementForm(prev => ({ ...prev, type: value }))}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="info">Info</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="success">Success</SelectItem>
                        <SelectItem value="error">Error</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1">Content</label>
                  <Textarea
                    value={announcementForm.content}
                    onChange={(e) => {
                      console.log('Content change:', e.target.value);
                      console.log('Previous content:', announcementForm.content);
                      setAnnouncementForm(prev => ({ ...prev, content: e.target.value }));
                    }}
                    onKeyDown={(e) => {
                      console.log('Key pressed:', e.key, 'Code:', e.code);
                    }}
                    placeholder="Announcement content"
                    rows={3}
                    className="text-xs"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Display Type</label>
                    <Select 
                      value={announcementForm.display_type}
                      onValueChange={(value) => setAnnouncementForm(prev => ({ ...prev, display_type: value }))}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sticky">Sticky at Top</SelectItem>
                        <SelectItem value="frequency">Every N Posts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {announcementForm.display_type === 'frequency' && (
                    <div>
                      <label className="block text-xs font-medium mb-1">Show Every N Posts</label>
                      <Input
                        type="number"
                        value={announcementForm.post_frequency}
                        onChange={(e) => setAnnouncementForm(prev => ({ ...prev, post_frequency: parseInt(e.target.value) || 10 }))}
                        placeholder="10"
                        min="1"
                        max="50"
                        className="h-8 text-xs"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Start Date (Optional)</label>
                    <Input
                      type="date"
                      value={announcementForm.start_date}
                      onChange={(e) => setAnnouncementForm(prev => ({ ...prev, start_date: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium mb-1">End Date (Optional)</label>
                    <Input
                      type="date"
                      value={announcementForm.end_date}
                      onChange={(e) => setAnnouncementForm(prev => ({ ...prev, end_date: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={announcementForm.is_active}
                      onChange={(e) => setAnnouncementForm(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-xs font-medium">Active</span>
                  </label>
                  
                  <div className="flex gap-2">
                    {editingAnnouncement && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          setEditingAnnouncement(null);
                          setAnnouncementForm({
                            title: '',
                            content: '',
                            type: 'info',
                            is_active: true,
                            start_date: '',
                            end_date: '',
                            display_type: 'frequency',
                            post_frequency: 10
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button type="submit" size="sm" className="h-7 text-xs">
                      <Plus className="h-3 w-3 mr-1" />
                      {editingAnnouncement ? 'Update' : 'Create'} Announcement
                    </Button>
                  </div>
                </div>
              </form>

              {/* Announcements List */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Existing Announcements</h3>
                {announcements.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-6">No announcements created yet.</p>
                ) : (
                  announcements.map((announcement) => (
                    <div key={announcement.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-xs font-medium">{announcement.title}</h4>
                            <Badge className={`${getAnnouncementTypeColor(announcement.type)} text-xs px-1 py-0`}>
                              {announcement.type}
                            </Badge>
                            <Badge variant={announcement.is_active ? "default" : "secondary"} className="text-xs px-1 py-0">
                              {announcement.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant="outline" className="text-xs px-1 py-0">
                              {announcement.display_type === 'sticky' ? 'Sticky' : `Every ${announcement.post_frequency} posts`}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-600 mb-1">{announcement.content}</p>
                          <div className="text-xs text-gray-500">
                            Created {new Date(announcement.created_at).toLocaleDateString()}
                            {announcement.start_date && (
                              <span> • Starts {new Date(announcement.start_date).toLocaleDateString()}</span>
                            )}
                            {announcement.end_date && (
                              <span> • Ends {new Date(announcement.end_date).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 w-6 p-0"
                            onClick={() => handleEditAnnouncement(announcement)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 w-6 p-0"
                            onClick={() => handleToggleAnnouncementStatus(announcement.id, announcement.is_active)}
                          >
                            {announcement.is_active ? (
                              <Ban className="h-3 w-3" />
                            ) : (
                              <CheckCircle className="h-3 w-3" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="communities" className="space-y-3">
          <CommunityRequestsAdmin />
        </TabsContent>

        <TabsContent value="grants" className="space-y-3">
          <GrantsImporter />
          <GrantsManagementTable />
        </TabsContent>
 
        <TabsContent value="suggestions" className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Lightbulb className="h-4 w-4" />
                Platform Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {suggestions.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-6">No suggestions submitted yet.</p>
                ) : (
                  suggestions.map((suggestion) => (
                    <div key={suggestion.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-xs font-medium">{suggestion.title}</h4>
                            <Badge className="text-xs px-1 py-0 bg-purple-100 text-purple-800">
                              {suggestion.category}
                            </Badge>
                            <Badge className={`${getSuggestionStatusColor(suggestion.status)} text-xs px-1 py-0`}>
                              {suggestion.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-600 mb-1">{suggestion.description}</p>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            <span>Submitted {new Date(suggestion.created_at).toLocaleDateString()}</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <MessageSquarePlus className="h-3 w-3" />
                              {suggestion.votes_count} votes
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <Select 
                            value={suggestion.status}
                            onValueChange={(value) => handleSuggestionStatusUpdate(suggestion.id, value)}
                          >
                            <SelectTrigger className="h-6 w-20 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in-progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Content Reports</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {reports.map((report) => (
                  <div key={report.id} className="border rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <Badge className={`${getReportStatusColor(report.status)} text-xs px-1 py-0`}>
                            {report.status}
                          </Badge>
                          <span className="text-xs text-gray-600">
                            {report.content_type} • {report.reason}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700 mb-1">
                          {report.description || 'No description provided'}
                        </p>
                        <div className="text-xs text-gray-500">
                          Reported {new Date(report.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      {report.status === 'pending' && (
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs text-green-600 hover:text-green-700"
                            onClick={() => handleReportAction(report.id, 'approve')}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Resolve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs text-red-600 hover:text-red-700"
                            onClick={() => handleReportAction(report.id, 'reject')}
                          >
                            <Ban className="h-3 w-3 mr-1" />
                            Dismiss
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">System Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <div className="p-3 border rounded-lg">
                <h3 className="text-sm font-medium mb-2">System Information</h3>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>Version: YS v1.2 - Social Media Integration</div>
                  <div>Environment: {import.meta.env.DEV ? 'Development' : 'Production'}</div>
                  <div>Last Updated: {new Date().toLocaleDateString()}</div>
                </div>
              </div>
              
              <div className="p-3 border rounded-lg">
                <h3 className="text-sm font-medium mb-2">Database Actions</h3>
                <div className="flex space-x-2">
                  <Button onClick={loadAdminData} variant="outline" size="sm" className="h-7 text-xs">
                    <Activity className="h-3 w-3 mr-1" />
                    Refresh Data
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
